import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import {
  ChannelsService,
  ChannelsResponse
} from 'src/app/core/services/channels.service';
import { FeaturesService } from 'src/app/core/services/features.service';
import { Subscription, finalize } from 'rxjs';
import { SubscriptionModel } from 'src/app/core/interfaces/subscription.interface';
import { MatDialog } from '@angular/material/dialog';
import { ChannelsDialogData } from 'src/app/features/secured-skeleton/channels/channels-dialog/interfaces/channels';
import { ChannelsDialogComponent } from 'src/app/features/secured-skeleton/channels/channels-dialog/channels-dialog.component';
import { ConfigDataService } from 'src/app/core/services/config-data.service';
import { LogoLibraryResponse } from 'src/app/core/interfaces/logoLibraryResponse';
import { DialogService } from '../../../../shared/secured-shared/dialog/dialog.service';
import { EpisodesCalendarViewComponent } from '../../extended-program-guide/episodes-calendar-view/episodes-calendar-view.component';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';

@Component({
    selector: 'app-channels-table',
    templateUrl: './channels-table.component.html',
    styleUrls: ['./channels-table.component.scss'],
    standalone: false
})
export class ChannelsTableComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) set sort(ms: MatSort) {
    this._sort = ms;
    this.setDataSourceAttributes();
  }

  get sort() {
    return this._sort;
  }

  @ViewChild('paginator') set paginator(pg: MatPaginator) {
    this._paginator = pg;
    this.setDataSourceAttributes();
  }

  get paginator() {
    return this._paginator;
  }

  constructor(
    private channelService: ChannelsService,
    private _snackBar: MatSnackBar,
    private featuresService: FeaturesService,
    private router: Router,
    public dialog: MatDialog,
    private epgDialog: DialogService,
    private configDataService: ConfigDataService
  ) {
    this.getChannelsSubs = channelService
      .getChannelsSubs()
      .subscribe((info: SubscriptionModel) => {
        if (
          info.channel === 'ChannelsHeaderComponent - ChannelsTableComponent' &&
          info.broadcastID === 'ChannelsHeaderComponent'
        ) {
          if (info.payload.refresh) {
            this.getChannels();
          } else {
            this.searchChannel(info.payload.searchValue);
          }
        }
      });
  }

  private epgSubscription: Subscription;
  private getChannelsSubs: Subscription;

  _sort: MatSort;
  _paginator: MatPaginator;
  pageSize = 25;
  pageIndex = 0;
  pageSizeOptions = [25, 50, 100];
  pageEvent: PageEvent;

  loadingData = true;
  channels: ChannelsResponse[] = [];
  dataSource = new MatTableDataSource<ChannelsResponse>();
  displayedColumns: string[] = [
    'name',
    'logo_id',
    'stream_source',
    'provider',
    'menu'
  ];
  logosLibrary: LogoLibraryResponse[] = [];

  @Input() assignMode: boolean = false;
  @Output() channelSelected = new EventEmitter<ChannelsResponse>();
  selection = new SelectionModel<ChannelsResponse>(false, []);

  ngOnInit(): void {
    this.getChannels();

    this.epgSubscription = this.featuresService.epgFeatureObs.subscribe(
      (epgEnabled) => {
        // navigate out if the EPG toggle gets disabled
        if (!epgEnabled) {
          this.router.navigate(['/']);
        }
      }
    );
  }

  async getChannels() {
    this.loadingData = true;

    if (this.configDataService.logoLibrary) {
      this.logosLibrary = this.configDataService.logoLibrary;
    } else {
      await this.configDataService.getLogosList();
      this.logosLibrary = this.configDataService.logoLibrary;
      this.logosLibrary.sort(function (a, b) {
        return a.name < b.name ? -1 : 1;
      });
    }

    // Enable add button after the logos library is loaded
    this.channelService.sendChannelSubs({
      channel: 'ChannelsTableComponent - ChannelsHeaderComponent',
      broadcastID: 'ChannelsTableComponent',
      payload: {
        enableAddButton: true
      }
    });

    this.channelService
      .getChannels()
      .pipe(finalize(() => (this.loadingData = false)))
      .subscribe({
        next: (channels) => {
          this.channels = channels;
          this.dataSource = new MatTableDataSource(this.channels);
          this.dataSource.filterPredicate = (
            data: ChannelsResponse,
            filter: string
          ): boolean => {
            return (
              data.name.toLowerCase().includes(filter.toLocaleLowerCase()) ||
              (data.stream_source || '')
                .toLowerCase()
                .includes(filter.toLocaleLowerCase())
            );
          };
          this.dataSource.sort = this.sort;
          // this.loadingData = false;
        },
        error: (error) => {
          this.openSnackBar(error.message, 'Ok');
        }
      });
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 6000
    });
  }

  openEditProgramGuideDialog(channel: ChannelsResponse) {
    this.epgDialog
      .openDialog(
        {
          content: EpisodesCalendarViewComponent,
          title: 'Edit Station Schedule',
          data: {
            channel,
            episodes: [],
            logosLibrary: this.logosLibrary
          }
        },
        '90vw'
      )
      .afterClosed()
      .subscribe({
        next: (value) => {
          if (!value) {
            return;
          }
          this.openSnackBar(
            'Program guide changes were successfully saved!',
            'Ok'
          );
        }
      });
  }

  openChannelsDialog(
    code: ChannelsDialogData['code'],
    channel: ChannelsResponse
  ) {
    const dialogRef = this.dialog.open(ChannelsDialogComponent, {
      panelClass: 'app-channels-panel-class',
      data: {
        code,
        channel,
        logosLibrary: this.logosLibrary
      }
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        let message = '';
        switch (result) {
          case 'channel-edited':
            message = 'Station edited successfully';
            break;
          case 'channel-deleted':
            message = 'Station deleted successfully';
            break;
        }
        this.getChannels();
        this.channelService.sendChannelSubs({
          channel: 'ChannelsTableComponent - ChannelsHeaderComponent',
          broadcastID: 'ChannelsTableComponent',
          payload: {
            clearSearch: true
          }
        });

        this.openSnackBar(message, 'Ok');
      }
    });
  }

  getDefaultLogoSrc() {
    const channelLogo = this.logosLibrary.find(
      (logo) => logo.is_default === true
    );
    return channelLogo?.path;
  }

  getLogoSrc(logoId: string): string {
    const channelLogo = this.logosLibrary.find((logo) => logo.id === logoId);
    if (channelLogo) {
      return channelLogo.path;
    }
    return this.getDefaultLogoSrc();
  }

  searchChannel(value: string) {
    this.dataSource.filter = value;
    this.setSelection();
  }

  setDataSourceAttributes() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  handlePageEvent(event: PageEvent) {
    this.pageEvent = event;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  setSelection(row?: ChannelsResponse) {
    if (row) {
      this.selection.toggle(row);
    } else {
      this.selection.clear();
    }
    this.channelSelected.emit(this.selection.selected[0]);
  }

  ngOnDestroy() {
    this.epgSubscription?.unsubscribe();
    this.getChannelsSubs?.unsubscribe();
  }
}
